// Organization Properties Tab - Lists all properties in the organization
import React from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface Property {
  id: number;
  marketingName: string;
  externalId?: number;
}

interface OrganizationPropertiesTabProps {
  organizationId: number;
  properties: Property[];
  isLoading: boolean;
}

export const OrganizationPropertiesTab: React.FC<OrganizationPropertiesTabProps> = ({
  organizationId,
  properties,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND} />
        <Text style={styles.loadingText}>Loading properties...</Text>
      </View>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.PROPERTY width={48} height={48} color={ADMIN_COLORS.TEXT.PRIMARY} />
        <Text style={styles.emptyTitle}>No Properties</Text>
        <Text style={styles.emptyMessage}>
          This organization doesn't have any properties assigned yet.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Properties</Text>
        <Text style={styles.sectionSubtitle}>
          {properties.length} {properties.length === 1 ? 'property' : 'properties'} in this organization
        </Text>

        <View style={styles.listContainer}>
          {properties.map((property) => (
            <View key={property.id} style={styles.propertyItem}>
              <View style={styles.propertyIconContainer}>
                <SVG_COMPONENTS.PROPERTY width={20} height={20} color={ADMIN_COLORS.TEXT.SECONDARY} />
              </View>
              <View style={styles.propertyInfo}>
                <Text style={styles.propertyName}>{property.marketingName}</Text>
                {property.externalId && (
                  <Text style={styles.propertyId}>ID: {property.externalId}</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 20,
  },
  listContainer: {
    gap: 8,
  },
  propertyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  propertyIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  propertyInfo: {
    flex: 1,
  },
  propertyName: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 2,
  },
  propertyId: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    textAlign: 'center',
  },
});

